import { FC, useState } from 'react';
import { Box, VStack } from '@chakra-ui/react';

import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import PasscodeInput from 'components/PasscodeInput';
import { ClearAllDigitsFn } from 'components/PasscodeInput/types';
import StepperProgressBar from 'components/Stepper/StepperProgressBar';
import { StepProps } from 'components/Stepper/types';
import { logEvent } from 'features/Analytics/analytics';
import ToggleShowPasscode from 'features/PaymentCards/pages/RequestNewCard/steps/SelectPinCode/components/ToggleShowPasscode';
import * as Layout from 'layouts/StepperLayout';

import { useRequestNewCardContext } from '../../hooks/useRequestNewCardContext';
import locales from './locales';

const ConfirmPinCode: FC<StepProps> = ({ goBack, goNext }) => {
  const {
    setSharedState,
    sharedState: { companyUserId, isPasscodeShown, pinCode },
  } = useRequestNewCardContext();
  const [showError, setShowError] = useState(false);

  const handleNext = (pinCodeConfirm: string) => {
    setSharedState((prevState) => ({
      ...prevState,
      pinCodeConfirm,
    }));

    logEvent({
      name: 'Card Request Pin Confirmed',
      properties: { companyUserId },
      usePrefix: false,
    });

    goNext();
  };

  const handleCompletePinCode = (
    code: string,
    clearAllDigits: ClearAllDigitsFn,
  ) => {
    setShowError(false);

    if (pinCode !== code) {
      setShowError(true);
      clearAllDigits({ andFocusFirstInput: true });
    } else {
      handleNext(code);
    }
  };

  return (
    <Layout.Root>
      <Layout.Body>
        <Layout.HeaderButtonsContainer>
          <BackButton onClick={goBack} />
        </Layout.HeaderButtonsContainer>

        <Layout.BodyScrollableSection>
          <VStack
            alignItems="stretch"
            paddingBottom="space-40"
            spacing="space-8"
          >
            <Heading size="lg">{locales.title}</Heading>
            <Text size="lg">{locales.subtitle}</Text>
          </VStack>

          <PasscodeInput
            hidden={!isPasscodeShown}
            onSubmit={handleCompletePinCode}
          />
          <Box marginTop="space-16">
            <ToggleShowPasscode />
          </Box>

          {showError ? (
            <Box paddingTop="space-40">
              <Callout variant="error">
                <Callout.Content>{locales.error}</Callout.Content>
              </Callout>
            </Box>
          ) : null}
        </Layout.BodyScrollableSection>

        <Layout.FooterContainer>
          <StepperProgressBar />

          <Layout.FooterButtonsContainer>
            <Button icon="arrow-right" isDisabled>
              {locales.request}
            </Button>
          </Layout.FooterButtonsContainer>
        </Layout.FooterContainer>
      </Layout.Body>
    </Layout.Root>
  );
};

export default ConfirmPinCode;
